/**
 * AuthScreen — the split frame every signed-out page sits in.
 * @file src/web/features/auth/components/AuthScreen.tsx
 *
 * @llm-rule WHEN: Wrapping forgot-password, reset-password, verify-email, register
 * @llm-rule NOTE: Sign-in builds this layout itself because its form needs the
 *   full treatment. Everything else in the auth flow gets the same frame from
 *   here, so moving between them never changes the furniture.
 */

import type { ReactNode } from 'react';
import { ArrowLeft, ScanFace } from 'lucide-react';
import { PRODUCT } from '../../../shared/brand';
import { AuthBrandPanel } from './AuthBrandPanel';

export function AuthScreen({
  title,
  subtitle,
  children,
  backHref = '/auth/login',
  backLabel = 'Back to sign in',
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  backHref?: string | null;
  backLabel?: string;
}) {
  return (
    <div className="flex min-h-screen bg-paper">
      <AuthBrandPanel />

      <div className="flex flex-1 flex-col">
        {/* The poster is hidden below lg, so the brand arrives as a band. */}
        <div className="aurora flex items-center gap-3 px-6 py-5 lg:hidden">
          <div className="relative z-10 grid size-9 place-items-center rounded-[11px] bg-white/10 ring-1 ring-inset ring-white/25">
            <ScanFace className="size-[19px] text-white" strokeWidth={1.8} />
          </div>
          <div className="relative z-10 leading-tight">
            <div className="text-[16px] font-semibold tracking-tight text-white">{PRODUCT.name}</div>
            <div className="text-[11.5px] text-white/50">{PRODUCT.tagline}</div>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
          <div className="w-full max-w-[380px]">
            <div className="anim-rise">
              <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.02em] text-ink">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-soft">{subtitle}</p>
              )}
            </div>

            <div className="anim-rise mt-7" style={{ ['--d' as string]: '80ms' }}>
              {children}
            </div>

            {backHref && (
              <a
                href={backHref}
                className="anim-rise mt-8 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-ink-faint transition-colors hover:text-ink"
                style={{ ['--d' as string]: '160ms' }}
              >
                <ArrowLeft className="size-3.5" strokeWidth={2} />
                {backLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
